import type { FC } from 'react';
import { useIntl } from 'react-intl';

import {
  xdlPaletteGrayCool04,
  xdlPalettePetrol03,
  xdlPalettePetrol05,
  xdlPalettePurpleRain03,
  xdlPalettePurpleRain05,
} from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import { useScreen } from '../../hooks/use-screen/use-screen';
import { getCompanyIndustryRoute } from '../../routes';
import type { IndustryItem } from '../../types';
import { IndustryCardSkeleton } from './industry-card.skeleton';
import * as Styled from './industry-card.styles';

export const localizationKeyBackground: {
  [id: string]: IndustryCardBackground;
} = {
  INDUSTRY_90000: xdlPaletteGrayCool04,
  INDUSTRY_160000: xdlPalettePurpleRain03,
  INDUSTRY_230000: xdlPalettePetrol03,
  INDUSTRY_40000: '#91D5C2',
  INDUSTRY_170000: xdlPalettePetrol05,
  INDUSTRY_20000: xdlPalettePurpleRain05,
  INDUSTRY_10000: xdlPaletteGrayCool04,
  INDUSTRY_210000: xdlPalettePetrol03,
  INDUSTRY_180000: '#F5AE87',
  INDUSTRY_200000: '#91D5C2',
  INDUSTRY_150000: xdlPalettePetrol05,
  INDUSTRY_140000: xdlPalettePurpleRain03,
  INDUSTRY_80000: '#F5AE87',
  INDUSTRY_220000: '#F5AE87',
  INDUSTRY_110000: '#91D5C2',
  INDUSTRY_60000: '#F5AE87',
  INDUSTRY_120000: xdlPalettePetrol05,
  INDUSTRY_30000: xdlPalettePurpleRain05,
  INDUSTRY_70000: xdlPalettePurpleRain05,
  INDUSTRY_190000: xdlPaletteGrayCool04,
  INDUSTRY_50000: xdlPalettePetrol03,
  INDUSTRY_100000: xdlPaletteGrayCool04,
  INDUSTRY_130000: xdlPalettePurpleRain03,
};

type IndustryCardBackground =
  | 'var(--xdlPaletteGrayCool04)'
  | 'var(--xdlPalettePetrol03)'
  | 'var(--xdlPalettePetrol05)'
  | 'var(--xdlPalettePurpleRain03)'
  | 'var(--xdlPalettePurpleRain05)'
  | '#F5AE87'
  | '#91D5C2';

type IndustryCardSubcomponents = {
  Skeleton: typeof IndustryCardSkeleton;
};

type IndustryCardProps = {
  item: IndustryItem;
  background?: IndustryCardBackground;
};
export const IndustryCard: FC<IndustryCardProps> &
  IndustryCardSubcomponents = ({ item, background }) => {
  const { isSmallScreen } = useScreen();
  const { formatMessage } = useIntl();

  const headerFontSize = isSmallScreen ? 'large' : 'xlarge';

  return (
    <Styled.Card
      background={background ?? localizationKeyBackground[item.key]}
      to={getCompanyIndustryRoute(item.id)}
      data-testid={`industryCard_${item.id}`}
    >
      <Styled.Headline size={headerFontSize}>{item.title}</Styled.Headline>
      <BodyCopy>
        {formatMessage(
          { id: 'INDUSTRY_CARD_TOTAL_COMPANIES' },
          { totalCompanies: item.totalCompanies }
        )}
      </BodyCopy>
    </Styled.Card>
  );
};

IndustryCard.Skeleton = IndustryCardSkeleton;
